/**
 * lib/arrearsRecipients.ts
 * ------------------------------------------------------------------
 * WHO A BULK ARREARS REMINDER ACTUALLY GOES TO.
 *
 * PURE. Pupils, holds and arrangements in; a list of recipients and the
 * reasons some of them are held back out. No Firestore and no clock — the
 * decision about who gets chased for money is one that has to be readable
 * and testable away from a database.
 *
 * TWO THINGS THE SCHOOL ASKED FOR, and they are the same problem seen twice:
 * a bulk send goes to a list somebody assembled by choosing a class, and the
 * list was wrong in both directions.
 *
 * A PUPIL WHO HAS LEFT IS NOT IN A CLASS. `currentClass` is not cleared when
 * a pupil leaves — the record keeps where they were, which is right — so a
 * former Form 5 pupil with an unpaid balance still answered to "Form 5 P"
 * and was swept into that class's reminder. The school found out the way
 * these things are always found out: a family that left got chased. Alumni
 * are now their own bucket. They still owe what they owe and can still be
 * chased deliberately; they are simply never included by picking a class,
 * because they are not in one.
 *
 * SOMEBODY WHO HAS MADE AN ARRANGEMENT MUST NOT BE CHASED AS IF THEY HAD
 * NOT. A payment plan or an academic-access release is the school having
 * already had the conversation, and a reminder afterwards reads as the
 * school going back on it. Those pupils are pre-deselected rather than
 * removed: the bursar can see them, see WHY they are held back, and put one
 * back in if that is the decision. Silently dropping them would be a
 * different kind of wrong — the bursar would never learn who was skipped.
 *
 * NOTHING HERE SENDS ANYTHING. It decides who is on the list. The list is
 * then shown, and the bursar presses the button.
 * ------------------------------------------------------------------ */

/** The alumni bucket, used where a class id would otherwise go. */
export const ALUMNI_BUCKET = '__alumni';

export interface RecipientStudent {
  id: string;
  fullName?: string;
  currentClass?: string;
  familyId?: string;
  status?: 'active' | 'left' | 'pending';
}

/**
 * Has this pupil left?
 *
 * Two ways of saying the same thing, because the school has used both: a
 * pupil marked `left` in the Promotion Wizard or on their profile, and a
 * pupil still parked in a class the school archived. Either answer means
 * they are not in a teaching class any more, so either must keep them out
 * of a class-based send.
 */
export function isAlumni(
  student: Pick<RecipientStudent, 'status' | 'currentClass'>,
  archivedClassIds: ReadonlySet<string>,
): boolean {
  if (student.status === 'left') return true;
  return !!student.currentClass && archivedClassIds.has(student.currentClass);
}

/**
 * The bucket a pupil is chased under.
 *
 * A former pupil answers to ALUMNI_BUCKET and to nothing else — not to the
 * class they used to be in, and not to that class's form. That is the whole
 * fix: the exclusion is a property of the pupil, not something the sender
 * has to remember.
 */
export function arrearsBucket(
  student: Pick<RecipientStudent, 'status' | 'currentClass'>,
  archivedClassIds: ReadonlySet<string>,
): string {
  return isAlumni(student, archivedClassIds) ? ALUMNI_BUCKET : (student.currentClass ?? '');
}

/**
 * Does this pupil belong to the form/class the bursar picked?
 *
 * `formOf` resolves a class id to its form; a bucket with no class resolves
 * to undefined and matches only "all". Alumni match the alumni filter and
 * nothing else, INCLUDING when the form filter is set — otherwise picking
 * "Form 5" would quietly bring back the very people this exists to keep out.
 */
export function matchesFilter(
  student: Pick<RecipientStudent, 'status' | 'currentClass'>,
  archivedClassIds: ReadonlySet<string>,
  formFilter: string,
  classFilter: string,
  formOf: (classId: string) => string | undefined,
): boolean {
  const alumni = isAlumni(student, archivedClassIds);

  if (classFilter === ALUMNI_BUCKET) return alumni;
  if (alumni) return false;

  const cls = student.currentClass ?? '';
  if (classFilter !== 'all' && cls !== classFilter) return false;
  if (formFilter !== 'all' && formOf(cls) !== formFilter) return false;
  return true;
}

/* ===================== who is held back, and why ====================== */

export type HoldBackReason = 'arrangement' | 'released' | 'no_email';

export const HOLD_BACK_LABEL: Record<HoldBackReason, string> = {
  arrangement: 'Payment arrangement in place',
  released: 'Released from the arrears hold',
  no_email: 'No parent email on file',
};

export interface RecipientRow {
  studentId: string;
  name: string;
  className: string;
  familyId?: string;
  overdue: number;
  /** Set when this pupil is pre-deselected. Never hidden — only unticked. */
  heldBack?: HoldBackReason;
}

export interface HoldBackInput {
  /** studentId -> academic-hold mode. 'release' means somebody let them through. */
  holdModes: Readonly<Record<string, string>>;
  /** familyIds carrying an ACTIVE payment arrangement. */
  arrangedFamilyIds: ReadonlySet<string>;
  /** studentIds with no reachable parent address. */
  unreachable?: ReadonlySet<string>;
}

/**
 * Mark the rows that should start unticked.
 *
 * ORDER MATTERS ONLY FOR THE LABEL. A pupil can be held back for more than
 * one reason; the most specific one is shown, because "payment arrangement"
 * tells the bursar something they can act on and "no email" does not.
 */
export function markHeldBack(rows: RecipientRow[], input: HoldBackInput): RecipientRow[] {
  return rows.map((r) => {
    if (r.familyId && input.arrangedFamilyIds.has(r.familyId)) {
      return { ...r, heldBack: 'arrangement' as const };
    }
    if (input.holdModes[r.studentId] === 'release') {
      return { ...r, heldBack: 'released' as const };
    }
    if (input.unreachable?.has(r.studentId)) {
      return { ...r, heldBack: 'no_email' as const };
    }
    const { heldBack: _drop, ...rest } = r;
    return rest;
  });
}

/** The ids that start ticked: everyone not held back. */
export function defaultSelection(rows: RecipientRow[]): Set<string> {
  return new Set(rows.filter((r) => !r.heldBack).map((r) => r.studentId));
}

/**
 * What the send button is about to do.
 *
 * Spelled out rather than left to a count, because "Email 34 families" and
 * "Email 34 of 51, holding back 17 who have arrangements" are different
 * sentences and only one of them lets somebody catch a mistake.
 */
export function describeSend(rows: RecipientRow[], selected: ReadonlySet<string>): string {
  const total = rows.length;
  const going = rows.filter((r) => selected.has(r.studentId)).length;
  if (total === 0) return 'Nobody in this class is in arrears.';
  if (going === 0) return `Nobody selected. ${total} in arrears.`;

  const held = rows.filter((r) => !selected.has(r.studentId));
  const byReason = new Map<string, number>();
  for (const r of held) {
    const key = r.heldBack ? HOLD_BACK_LABEL[r.heldBack].toLowerCase() : 'deselected by hand';
    byReason.set(key, (byReason.get(key) ?? 0) + 1);
  }

  if (!held.length) return `Emailing all ${going} in arrears.`;
  const why = [...byReason.entries()].map(([k, n]) => `${n} ${k}`).join(', ');
  return `Emailing ${going} of ${total}. Holding back ${why}.`;
}

/**
 * Guard the send itself.
 *
 * The selection is what the screen shows; this is what actually goes. A
 * pupil who is not on the list cannot be emailed by a stale selection
 * surviving a filter change, which is how the wrong person gets chased.
 */
export function recipientsToSend(
  rows: RecipientRow[],
  selected: ReadonlySet<string>,
): string[] {
  return rows.filter((r) => selected.has(r.studentId)).map((r) => r.studentId);
}
